/**
 * The swimmer-and-ship film, under the savings card: "why learn software
 * engineering at all, in the age of AI" (components/explainer/swimmer-and-ship.tsx).
 * It was the first of two narrated bands under the hero from 2026-10-03; it
 * came back folded when the front door became "give this to your agent", and
 * since 2026-10-31 it is always open (owner: folded, it was "not really
 * obvious at all").
 *
 * Open is not the same as loaded. The scenes are a separate chunk fetched
 * when the band comes within a screen of the viewport, so the first view
 * still costs nothing for it, and the voice only once somebody presses play
 * on the film's own cover: nothing starts by itself. No `chapter` is passed,
 * so a watch records nothing in the funnel; the explainer events name a
 * chapter's first level, and this has none.
 */
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { PAGE_EXPLAINERS } from '../explainer';
import { useLocale } from '../../state/useLocale';
import { landingFrontCopy } from '../../lib/strings/landing-front';

const AgeOfAi = lazy(PAGE_EXPLAINERS['swimmer-and-ship']);

/** The band's address, for a link that wants to point at the film. */
export const WHY_VIDEO_ID = 'why-steer-agents';

export function WhyVideo() {
  const { locale } = useLocale();
  const s = landingFrontCopy[locale];
  const [near, setNear] = useState(false);
  const band = useRef<HTMLElement>(null);
  useEffect(() => {
    const node = band.current;
    if (!node) return;
    // Without an observer there is no way to wait for the scroll, so load.
    if (typeof IntersectionObserver !== 'function') {
      setNear(true);
      return;
    }
    const watch = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setNear(true);
        watch.disconnect();
      }
    }, { rootMargin: '100% 0px' });
    watch.observe(node);
    return () => watch.disconnect();
  }, []);
  return (
    <section className="why-video" id={WHY_VIDEO_ID} ref={band} aria-labelledby={WHY_VIDEO_ID + '-title'}>
      <div className="why-video__head">
        <p className="label">{s.filmLabel}</p>
        <h2 id={WHY_VIDEO_ID + '-title'}>{s.filmTitle}</h2>
        <p className="lede">{s.filmLede}</p>
      </div>
      <div className="why-video__film">
        {near && (
          <Suspense fallback={null}>
            <AgeOfAi />
          </Suspense>
        )}
      </div>
    </section>
  );
}
